import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, FormLabel, TextField, Grid, Button, Chip, Paper, Tab, Tabs } from '@mui/material';
import Section from './Common/Section';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MenuItem from '@mui/material/MenuItem';
import * as taskDetailConstants from '../constants/taskDetailConstants';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TaskButtonBar from './TaskDetailComponents/TaskButtonBar';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    AutoLink,
    BlockQuote,
    Bold,
    ClassicEditor,
    Essentials, FontFamily, FontSize,
    Heading,
    Indent,
    Italic,
    Link, LinkEditing, LinkUI,
    List, MediaEmbed,
    Mention,
    Paragraph, Table,
    Undo,
} from 'ckeditor5';
import ConfirmDeleteDialog from './Modal/ConfirmDelete';
import TaskHistoryButtonBar from './TaskDetailComponents/TaskHistoryButtonBar';
import FileUploadModal from './Modal/FileUploadModal';
import fileUploadService from '../api/fileUploadService';
import TaskApiService from '../api/TaskApiService';
import CircularProgress from '@mui/material/CircularProgress';
import TaskRelationshipModal from './Modal/TaskRelationshipModal';
import TaskSubscriptionModal from './Modal/TaskSubscriptionModal';
import './Tasks/Tasks.css';
import TaskComment from './TaskDetailComponents/TaskComment';
import TaskAttachment from './TaskDetailComponents/TaskAttachment';
import dayjs from 'dayjs';
import EditTaskRecurrenceModal from './Modal/RecurringTasks/EditTaskRecurrenceModal';
import {
    convertRecurrenceStringToDescription,
} from '../constants/RecurringTaskConstants';
import Typography from '@mui/material/Typography';
import RecurrencePastCompletionsModal from './Modal/RecurringTasks/RecurrencePastCompletionsModal';
import { Textarea } from '@mui/joy';
import StatusApiService from '../api/StatusApiService';
import UserApiService from '../api/UserApiService';
import CategoryApiService from '../api/CategoryApiService';
import PriorityApiService from '../api/PriorityApiService';
import OrgApiService from '../api/OrgApiService';
import TaskHistoryDto from '../dtos/Task/TaskHistoryDto';
import LocationApiService from '../api/LocationApiService';
import TaskRecurrenceDto from '../dtos/Task/TaskRecurrenceDto';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from './Utils/AuthUtil';
import { useSnackbar } from './Common/SnackbarProvider';

const HISTORY_TYPE_ATTACHMENT = 'file';

const taskApiService = new TaskApiService();
const statusApiService = new StatusApiService();
const categoryApiService = new CategoryApiService();
const priorityApiService = new PriorityApiService();
const orgApiService = new OrgApiService();
const locationApiService = new LocationApiService();
const userApiService = new UserApiService();

const Task = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const params = useParams();
    const [taskId] = useState(params.taskId);
    const [nextTaskId, setNextTaskId] = useState();
    const [previousTaskId, setPreviousTaskId] = useState();
    const [taskLoading, setLoading] = useState(true);
    const [taskSaving, setTaskSaving] = useState(false);

    const [currentComment, setCurrentComment] = useState('');
    const [taskHistory, setTaskHistory] = useState([]);
    const [showImages, setShowImages] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    // Button labels are derived from the show* booleans so they re-render on
    // locale change. Storing the translated text in state (the old pattern)
    // captures one language at mount time and breaks when the user switches.
    const inlineImagesButtonText = showImages ? t('taskPage.button.hideInlineImages') : t('taskPage.button.showInlineImages');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [openRelationshipModal, setShowRelationshipModal] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [showSubscribersModal, setShowSubscribersModal] = useState(false);
    const [showEditRecurrenceModal, setShowEditRecurrenceModal] = useState(false);
    const [showPastRecurrencesModal, setShowPastRecurrencesModal] = useState(false);
    const [showRecurrenceFileUploadModal, setShowRecurrenceFileUploadModal] = useState(false);
    const [showConfirmCopy, setShowConfirmCopy] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availablePriorities, setAvailablePriorities] = useState([]);
    const [availableOrganizations, setAvailableOrganizations] = useState([]);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const [availableProjects, setAvailableProjects] = useState([]);
    const [currentOrganization, setCurrentOrganization] = useState();
    const [componentKey, setComponentKey] = useState(0);
    const [recurringDueDate, setRecurringDueDate] = useState('');

    const [currentTask, setCurrentTask] = useState();
    const [originalTask, setOriginalTask] = useState();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const navigate = useNavigate();

    const userToken = useSelector((state) => state.correcttrack.userToken);

    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);
    const isAdmin = useMemo(() => AuthUtil.isAdmin(userToken), [userToken]);
    const userId = useMemo(() => AuthUtil.getUserId(userToken), [userToken]);
    const projectUserXRefs = useMemo(() => AuthUtil.getProjectUserXRefs(userToken), [userToken]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hasChanges = useMemo(() => JSON.stringify(originalTask) !== JSON.stringify(currentTask) || taskId === '0' || taskId === '', [currentTask, originalTask]);
    const currentStatus = useMemo(
        () => availableStatuses.find(s => s.stId === currentTask?.statusId) ?? null,
        [availableStatuses, currentTask?.statusId]
    );
    const isRecurringTask = useMemo(() => currentTask?.category?.recurring ?? false, [currentTask?.category?.recurring]);
    const taskRecurrenceId = useMemo(() =>
        currentTask && currentTask.taskRecurrences && currentTask.taskRecurrences.length > 0 ?
            (currentTask.taskRecurrences.length === 1 ? currentTask.taskRecurrences[0].id :
            currentTask.taskRecurrences.find((recurrence) => !recurrence.completed).id)
            :
            0, [currentTask]);

    const recurrenceString = useMemo(() =>
        currentTask && currentTask.taskRecurrences && currentTask.taskRecurrences.length > 0 ?
            (currentTask.taskRecurrences.length === 1 ? currentTask.taskRecurrences[0].recurrenceString :
            currentTask.taskRecurrences.find((recurrence) => !recurrence.completed).recurrenceString)
            :
            '', [currentTask]);

    const recurrenceDescriptionText = useMemo(() => {
        if(isRecurringTask) {
            return convertRecurrenceStringToDescription(recurrenceString);
        }
        return '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recurrenceString]);

    const canModifyFields = useMemo(() => {
            return (isSuperAdmin ||
            (currentTask?.reportedUserId === userId) ||
            (projectUserXRefs && projectUserXRefs.length > 0 && projectUserXRefs?.find((permission) => permission.projectId === currentTask?.projectId)?.permissionLevel > 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ,[projectUserXRefs, isSuperAdmin, currentTask?.reportedUserId]);

    const canCreateTasks = useMemo(() => {
        return (isSuperAdmin || isAdmin ||
            (projectUserXRefs && projectUserXRefs.some((permission) => permission.permissionLevel > 1)));
    }, [projectUserXRefs, isSuperAdmin, isAdmin]);

    async function handleCopyTask (confirmed) {
        if(confirmed) {
            const newTask = await taskApiService.copyTask(taskId);

            if(newTask && newTask.success) {
                snackbar.success(t('taskPage.toast.copied', { id: newTask.data }));
            } else if(newTask && newTask.message) {
                snackbar.error(t('taskPage.toast.copyError', { error: newTask.message }));
            } else {
                snackbar.error(t('taskPage.toast.copyError', { error: newTask }));
            }
        }

        setShowConfirmCopy(false);
    }

    function showCopyTaskDialog() {
        setShowConfirmCopy(true);
    }

    function handlePrintTask () {
        window.open('/PrintTask/' + taskId);
    }

    function handleSubscribers () {
        setShowSubscribersModal(true);
    }

    function handleCloseSubscribers () {
        setShowSubscribersModal(false);
    }

    async function handleGetNotifications () {
        if (isSubscribed) {
            const unsubscribe = await taskApiService.unsubscribeFromTask(taskId, userId);
            if (unsubscribe.success) {
                setIsSubscribed(false);
                snackbar.warning(t('taskPage.toast.unsubscribed'));
            } else {
                snackbar.error(unsubscribe.message);
            }
        } else {
            const subscribe = await taskApiService.subscribeToTask(taskId, userId);
            if (subscribe.success) {
                setIsSubscribed(true);
                snackbar.success(t('taskPage.toast.subscribed'));
            } else {
                snackbar.error(subscribe.message);
            }
        }
    }

    function handleRelationships () {
        setShowRelationshipModal(true);
    }

    function handleCloseRelationships () {
        setShowRelationshipModal(false);
    }

    function handleSendEmail () {

    }

    function handleAddAttachment () {
        setShowUploadModal(true);
    }

    function handleNewTask () {
        window.location.href = '/Task/';
    }

    function compareTaskHistory (a, b) {
        if (a.taskChangeTime > b.taskChangeTime) {
            return -1;
        }
        if (a.taskChangeTime < b.taskChangeTime) {
            return 1;
        }
        return 0;
    }

    async function onDeleteClick (value) {
        if (value) {
            const result = await taskApiService.deleteTask(taskId);
            if (result) {
                navigate('/Tasks');
            }
        } else {
            setShowDeleteModal(false);
        }
    }

    function handleFileUpload (event) {
        if (event) {
            fileUploadService.uploadTaskAttachment(event, taskId).then(() => {
                window.location.href = '/Task/' + taskId;
            });
        }

        setShowUploadModal(false);
    }

    function handleRecurrenceFileUpload(event) {
        if (event) {
            fileUploadService.uploadAttachmentForTaskRecurrence(event, taskRecurrenceId).then(() => {
                window.location.href = `/Task/${taskId}`;
            });
        }
        setShowRecurrenceFileUploadModal(false);
    }

    /**
     *
     * @param customColumn {TaskCustomColumnDto}
     */
    function renderCustomColumn(customColumn) {
        return(
            <Grid item xs={12} sx={gridItemSx}>
                <Grid item>
                    <FormLabel>{customColumn.customColumn.customColumnName}</FormLabel>
                </Grid>
                <Grid item xs={12}>
                    {renderCustomColumnData(customColumn)}
                </Grid>
            </Grid>
        );
    }

    function getUsernameById(userId) {
        const username = availableUsers.find((user) => user.userId === parseInt(userId));
        return username?.username ?? '';
    }

    function renderCustomColumnData(customColumn) {
        function updateValue (newValue, customColumnId) {
            const thisTask = {...currentTask};

            thisTask.customColumnData.find((ccd => ccd.customColumn.customColumnId === customColumnId)).columnValue = newValue;

            setCurrentTask(thisTask);
        }

        switch (customColumn.customColumn.inputType.toLowerCase()) {
        case(taskDetailConstants.DATE):
            // Dates are stored as ISO yyyy-MM-dd. new Date() would read that as UTC midnight
            // and show the previous day west of Greenwich; dayjs parses a date-only string as
            // local, and is what the picker's adapter uses anyway.
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        views={['year', 'month', 'day']}
                        disabled={!canModifyFields}
                        inputFormat="MM/DD/YYYY"
                        value={customColumn.columnValue ? dayjs(customColumn.columnValue) : null}
                        onChange={(newValue) => {
                            updateValue(newValue, customColumn.customColumn.customColumnId)
                        }}
                        renderInput={(params) => <TextField {...params} fullWidth error={false}/>}
                    /></LocalizationProvider>
            );
        case(taskDetailConstants.OPTION):
            if(customColumn.customColumn.options === '%users%') {
                return(<TextField
                    select
                    fullWidth
                    disabled={!canModifyFields}
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => <Chip key={value} label={getUsernameById(value)} size="small"/>)}
                            </Box>
                        ),
                    }}
                    value={customColumn.columnValue ? customColumn.columnValue.split(',').filter(Boolean) : []}
                    onChange={(event) => {
                        const next = Array.isArray(event.target.value) ? event.target.value : [event.target.value];
                        updateValue(next.join(','), customColumn.customColumn.customColumnId);
                    }}
                >
                    {availableUsers.map((user) => <MenuItem key={user.userId} value={user.userId}>{user.username}</MenuItem>)}
                </TextField>);
            } else {
                const localizedOptions = customColumn.customColumn.localizedOptions ?? {};
                return(
                    <TextField
                        select
                        fullWidth
                        disabled={!canModifyFields}
                        value={customColumn.columnValue ?? ''}
                        onChange={(event) => updateValue(event.target.value, customColumn.customColumn.customColumnId)}
                    >
                        {customColumn.customColumn.options.toLowerCase() === '%user%'
                            ? availableUsers.map((user) => <MenuItem key={user.userId} value={user.username}>{user.username}</MenuItem>)
                            // Option `value` stays as the source key so saves/round-trips work;
                            // display label comes from the server-resolved localizedOptions dict.
                            : customColumn.customColumn.options.split('|').map((value, index) => <MenuItem key={index} value={value}>{localizedOptions[value] ?? value}</MenuItem>)}
                    </TextField>
                );
            }
        case(taskDetailConstants.TEXT_FIELD):
            return(
                <TextField
                    fullWidth
                    disabled={!canModifyFields}
                    value={customColumn.columnValue ?? ''}
                    onChange={(event) => updateValue(event.target.value, customColumn.customColumn.customColumnId)}
                />
            );
        case(taskDetailConstants.INT):
            return(
                <TextField
                    fullWidth
                    disabled={!canModifyFields}
                    value={customColumn.columnValue ?? ''}
                    type="number"
                    onChange={(event) => updateValue(event.target.value, customColumn.customColumn.customColumnId)}
                />
            );
        case(taskDetailConstants.TEXT_AREA):
            return(
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={!canModifyFields}
                    value={customColumn.columnValue ?? ''}
                    onChange={(event) => updateValue(event.target.value, customColumn.customColumn.customColumnId)}
                />
            );
        default:
            return (
                <TextField
                    fullWidth
                    disabled={!canModifyFields}
                    value={customColumn.columnValue ?? ''}
                    onChange={(event) => updateValue(event.target.value, customColumn.customColumn.customColumnId)}
                />
            );
        }
    }

    function renderTaskHistory(historyItem, index) {
        return(
            historyItem.taskChangeType === HISTORY_TYPE_ATTACHMENT ?
                <TaskAttachment
                    showImages={showImages}
                    key={index}
                    taskChange={historyItem}
                    setTaskChange={(value) => {
                        const copyTaskHistory = [...taskHistory];
                        const thisHistory = copyTaskHistory.find((history) => history.taskChangeId === historyItem.taskChangeId);
                        thisHistory.taskChange = value;
                        setTaskHistory(copyTaskHistory);
                    }}
                    handleDeleted={() => {
                        let copyTaskHistory = [...taskHistory];
                        const thisHistoryIndex = copyTaskHistory.findIndex((history) => history.taskChangeId === historyItem.taskChangeId);
                        copyTaskHistory.splice(thisHistoryIndex, 1);

                        setTaskHistory(copyTaskHistory);
                    }}
                />
                :
                <TaskComment
                    handleDeleted={() => {
                        let copyTaskHistory = [...taskHistory];
                        const thisHistoryIndex = copyTaskHistory.findIndex((history) => history.taskChangeId === historyItem.taskChangeId);
                        copyTaskHistory.splice(thisHistoryIndex, 1);

                        setTaskHistory(copyTaskHistory);
                    }}
                    key={index}
                    taskChange={historyItem}
                    setTaskChange={(value) => {
                        const copyTaskHistory = [...taskHistory];
                        const thisHistory = copyTaskHistory.find((history) => history.taskChangeId === historyItem.taskChangeId);
                        thisHistory.taskChange = value;
                        setTaskHistory(copyTaskHistory);
                    }}
                />
        );
    }

    function showDeleteDialog () {
        setShowDeleteModal(true);
    }

    async function handleOrganizationChange(event) {
        if(isSuperAdmin) {
            const response = await locationApiService.getFullLocationsForOrg(event.target.value);
            setAvailableProjects(response.data);
            setCurrentOrganization(event.target.value);

            const selectableUsers = await userApiService.getSelectableUsersForOrg(event.target.value);

            setAvailableUsers(selectableUsers.data);
        }
    }

    function handleProjectChange(event) {
        const thisTask = {...currentTask};
        const selectedProject = availableProjects.find((project) => project.pjId === event.target.value);
        thisTask.project = selectedProject;
        thisTask.projectId = event.target.value;

        setCurrentTask(thisTask);
    }

    async function handleCategoryChange(event) {
        const thisTask = {...currentTask};
        thisTask.categoryId = event.target.value;
        const selectedCategory = availableCategories.find((category) => category.ctId === event.target.value);
        thisTask.category = selectedCategory;

        const taskIdToSend = parseInt(taskId) > 0 ? taskId : 0;

        const newCustomColumns = await taskApiService.getCustomColumnsForCategoryChange(taskIdToSend, event.target.value);

        thisTask.customColumnData = newCustomColumns.data;

        setCurrentTask(thisTask);
    }

    function handlePriorityChange(event) {
        const thisTask = {...currentTask};
        thisTask.priorityId = event.target.value;
        const selectedPriority = availablePriorities.find((priority) => priority.prId === event.target.value);
        thisTask.priority = selectedPriority;
        setCurrentTask(thisTask);
    }

    async function saveTaskComment() {
        const newComment = new TaskHistoryDto();
        newComment.taskChange = currentComment;
        newComment.taskId = taskId;
        newComment.taskChangeType = 'comment';
        newComment.taskChangeUser = '';
        const response = await taskApiService.addTaskComment(newComment);

        if(response) {
            const currentHistory = [...taskHistory];
            currentHistory.push(response);
            setTaskHistory(currentHistory.sort(compareTaskHistory));
            setCurrentComment('');
            snackbar.success(t('taskPage.toast.commentSaved'));
            setComponentKey(componentKey === 100 ? 0 : componentKey + 1);
        } else {
            snackbar.error(t('taskPage.toast.commentError'));
        }
    }

    function handleInlineImagesButton() {
        setShowImages(!showImages);
    }

    function handleRecurrenceNotesChange(event) {
        const thisTask = {...currentTask};

        const currentRecurrence = thisTask.taskRecurrences.find((recurrence) => !recurrence.completed);
        currentRecurrence.completionNotes = event.target.value;

        setCurrentTask(thisTask);
    }

    async function handleRecurrenceStringChange(value) {
        const thisTask = {...currentTask};

        const noPreviousRecurrence = thisTask.taskRecurrences.length === 0;

        const newDueDate = await taskApiService.getNextRecurrenceForChange(taskId, value);
        setRecurringDueDate(newDueDate.data.toString().split('T')[0]);

        if(noPreviousRecurrence) {
            const newRecurrence = new TaskRecurrenceDto();
            newRecurrence.recurrenceString = value;
            newRecurrence.taskId = taskId;
            newRecurrence.due = newDueDate.data;

            thisTask.taskRecurrences.push(newRecurrence);
        } else {
            const currentRecurrence = thisTask.taskRecurrences.length > 0 ? thisTask.taskRecurrences.find((recurrence) => !recurrence.completed) : new TaskRecurrenceDto();

            currentRecurrence.recurrenceString = value;
        }

        setCurrentTask(thisTask);
    }

    function getCurrentCompletionNotes() {
        const currentRecurrence = currentTask.taskRecurrences.find((recurrence) => !recurrence.completed);
        return currentRecurrence.completionNotes;
    }

    async function saveTask() {
        setTaskSaving(true);
        const isNewTask = !(parseInt(taskId) > 0);
        if(!isNewTask && JSON.stringify(originalTask) === JSON.stringify(currentTask)) {
            snackbar.error(t('taskPage.toast.noChanges'));
            return;
        }

        if(!validateTask()) {
            snackbar.error(t('taskPage.toast.validationFailed'));
        }

        if(isNewTask) {
            const newTask = await taskApiService.addNewTask(currentTask);

            if(newTask) {
                window.location.href=`/Task/${newTask.id}`;
            }
        } else {
            const response = await taskApiService.updateTask(taskId, currentTask);

            if(response && response.success) {
                snackbar.success(t('taskPage.toast.saved'));
                setOriginalTask(JSON.parse(JSON.stringify(currentTask)));

                const newTaskHistoryResponse = await taskApiService.getTaskHistory(taskId);

                setTaskHistory(newTaskHistoryResponse.data);
            } else {
                snackbar.error(t('taskPage.toast.saveError', { error: response ? (response.message ?? response) : '' }));
            }
        }

        setTaskSaving(false);
    }

    function validateTask() {
        return true;
    }

    async function loadTask() {
        let response;
        if(parseInt(taskId) > 0) {
            response = await taskApiService.getTask(taskId);
            if(response.message === 'Unauthorized') {
                snackbar.error(t('taskPage.toast.taskNotFoundOrUnauthorized'));
                navigate('/Tasks');
            }
            if(isSuperAdmin) {
                setCurrentOrganization(response.data.project.pjOrgDto.ogId);
            }
        } else {
            response = await taskApiService.getNewTask();
        }
        setOriginalTask(response.data);
        if(response.data.isRecurring) {
            setRecurringDueDate(response.data && response.data.taskRecurrences && response.data.taskRecurrences.length > 0 ?
                (response.data.taskRecurrences.length === 1 ? response.data.taskRecurrences[0].due.toString().split('T')[0] :
                response.data.taskRecurrences.find((recurrence) => !recurrence.completed).due.toString().split('T')[0])
                :
                '');
        }
        setCurrentTask(JSON.parse(JSON.stringify(response.data)));
    }

    async function loadCategories() {
        const categories = await categoryApiService.GetCategories();
        setAvailableCategories(categories.data);
    }

    async function loadPriorities() {
        const priorities = await priorityApiService.GetPriorities();
        setAvailablePriorities(priorities.data);
    }

    const getAvailableUsers = async () => {
        if(parseInt(taskId) > 0) {
            const response = await taskApiService.getAvailableUsersForTask(taskId);
            if (response.success) {
                setAvailableUsers(response.data);
            } else {
                snackbar.error(t('taskPage.toast.usersError', { error: response.message }));
            }
        } else {
            const response = await userApiService.getUsersForNewTask();
            if(response.success) {
                setAvailableUsers(response.data);
            } else {
                snackbar.error(t('taskPage.toast.usersError', { error: response.message }));
            }
        }
    }

    async function loadStatuses() {
        const response = await statusApiService.getStatuses();
        setAvailableStatuses(response.data);
    }

    async function loadOrganizations() {
        const response = await orgApiService.getOrgs();
        setAvailableOrganizations(response.data);
    }

    async function loadTaskHistory() {
        if(parseInt(taskId) > 0) {
            const response = await taskApiService.getTaskHistory(parseInt(taskId));
            setTaskHistory(response.data);
        }
    }

    async function loadProjects() {
        let response;
        if(parseInt(taskId) > 0) {
            response = await locationApiService.getPossibleFullLocationsForTask(taskId);
        } else {
            response = await locationApiService.getPossibleFullLocationsForNewTask();
        }

        setAvailableProjects(response.data);
    }

    async function loadNextTaskId() {
        if(parseInt(taskId) > 0) {
            const response = await taskApiService.getNextTaskId(taskId);

            setNextTaskId(response);
        }
    }

    async function loadPreviousTaskId() {
        if(parseInt(taskId) > 0) {
            const response = await taskApiService.getPreviousTaskId(taskId);

            setPreviousTaskId(response);
        }
    }

    useEffect(() => {
        const isNewTask = !taskId || taskId === '0' || taskId === '';
        if (isNewTask && !canCreateTasks) {
            snackbar.error(t('taskPage.toast.noPermission'));
            navigate('/Tasks');
            return;
        }
        Promise.all([
            loadPriorities(),
            loadCategories(),
            loadTask(),
            getAvailableUsers(),
            loadStatuses(),
            loadTaskHistory(),
            loadProjects(),
            loadNextTaskId(),
            loadPreviousTaskId()
        ]).then(() => setLoading(false));
        if(isSuperAdmin) {
            void loadOrganizations();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId, componentKey]);

    const gridItemSx = { marginBottom: '30px'}

    const isExistingTask = taskId !== undefined && parseInt(taskId) !== 0;
    const commentItems = taskHistory.filter(h => h.taskChangeType === 'comment');
    const historyItems = taskHistory.filter(h => h.taskChangeType !== 'comment' && h.taskChangeType !== HISTORY_TYPE_ATTACHMENT);
    const attachmentItems = taskHistory.filter(h => h.taskChangeType === HISTORY_TYPE_ATTACHMENT);

    return (
        <Box>
            {taskLoading ?
                <Box sx={{ position: 'fixed', top: '50%', left: '50%', display: 'flex' }}>
                    <CircularProgress/>
                </Box>
                :
                <Box>
                    {/* ── Sticky header bar ─────────────────────────── */}
                    <Paper
                        elevation={2}
                        sx={{
                            position: 'sticky',
                            // Sticks directly beneath the app header, whose height varies with the impersonation banner.
                            top: 'var(--ct-header-h, 56px)',
                            zIndex: 2,
                            mb: 2,
                            px: 2,
                            py: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {/* Row 1: identity on the left, save on the right */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {parseInt(taskId) > 0 ? t('taskPage.field.taskId', { id: taskId }) : t('taskPage.title.new')}
                            </Typography>
                            {currentStatus && (
                                <Chip
                                    label={currentStatus.localizedName ?? currentStatus.stName}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                variant="contained"
                                disabled={!hasChanges || taskSaving || !canModifyFields}
                                onClick={saveTask}
                            >
                                {taskSaving ? t('taskPage.button.saving') : t('common.save')}
                            </Button>
                        </Box>

                        {/* Row 2: the action bar, on its own centered line */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', '& .task-button-bar': { marginTop: 0 } }}>
                            <TaskButtonBar
                                previousTaskId={previousTaskId}
                                nextTaskId={nextTaskId}
                                onDeleteClick={showDeleteDialog}
                                handleCopyTask={showCopyTaskDialog}
                                handlePrintTask={handlePrintTask}
                                handleSubscribers={handleSubscribers}
                                handleGetNotifications={handleGetNotifications}
                                handleRelationships={handleRelationships}
                                handleSendEmail={handleSendEmail}
                                handleAddAttachment={handleAddAttachment}
                                currentTaskId={taskId === '' || taskId === '0' ? 0 : parseInt(taskId)}
                                handleNewTask={handleNewTask}
                                isAdmin={isAdmin}
                                isSubscribed={isSubscribed}
                                canModifyFields={canModifyFields}
                                canCreateTasks={canCreateTasks}
                            />
                        </Box>
                    </Paper>

                    {/* ── Main two-column grid ──────────────────────── */}
                    <Grid container spacing={2} sx={{ px: 1 }}>
                        {/* Left column */}
                        <Grid item xs={12} md={8}>
                            <Section title={t('taskPage.section.identity')}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('fieldName.description')}
                                        disabled={!canModifyFields}
                                        value={currentTask?.shortDescription ?? ''}
                                        onChange={(event) => {
                                            const thisTask = {...currentTask};
                                            thisTask.shortDescription = event.target.value;
                                            setCurrentTask(thisTask);
                                        }}
                                    />
                                </Grid>
                            </Section>

                            <Section title={t('taskPage.section.classification')}>
                                {isSuperAdmin && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label={t('fieldName.organization')}
                                            disabled={!canModifyFields}
                                            value={currentOrganization ?? ''}
                                            onChange={handleOrganizationChange}
                                        >
                                            {availableOrganizations.map((org, index) => (
                                                <MenuItem key={index} value={org.ogId}>{org.ogName}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('taskPage.field.projectLocation')}
                                        disabled={!canModifyFields}
                                        value={currentTask?.project?.pjId ?? 0}
                                        onChange={handleProjectChange}
                                    >
                                        {availableProjects?.map((project, index) => (
                                            <MenuItem key={index} value={project.pjId}>{project.pjName}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('fieldName.category')}
                                        disabled={!canModifyFields}
                                        value={currentTask?.categoryId ?? ''}
                                        onChange={handleCategoryChange}
                                    >
                                        {availableCategories.sort((a, b) => a.ctSortSeq - b.ctSortSeq).map((category, index) => (
                                            <MenuItem key={index} value={category.ctId}>{category.localizedName ?? category.ctName}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('fieldName.priority')}
                                        disabled={!canModifyFields}
                                        value={currentTask?.priorityId ?? ''}
                                        onChange={handlePriorityChange}
                                    >
                                        {availablePriorities.sort((a, b) => a.prSortSeq - b.prSortSeq).map((priority, index) => (
                                            <MenuItem key={index} value={priority.prId}>{priority.localizedName ?? priority.prName}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                {currentTask?.customColumnData.map((customColumn) => renderCustomColumn(customColumn))}
                            </Section>

                            {isRecurringTask && (
                                <Section title={t('taskPage.section.recurrence')}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Button disabled={!canModifyFields} variant="contained" onClick={() => setShowEditRecurrenceModal(true)}>
                                                {t('taskPage.button.setRecurrence')}
                                            </Button>
                                            <Typography sx={{ fontStyle: 'italic' }}>{recurrenceDescriptionText}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="overline" color="text.secondary">{t('fieldName.dueDate')}</Typography>
                                        <Typography>{recurringDueDate}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Textarea
                                            disabled={!canModifyFields}
                                            minRows={2}
                                            sx={{ width: '100%' }}
                                            placeholder={t('taskPage.field.currentRecurrenceNotes')}
                                            value={getCurrentCompletionNotes}
                                            onChange={handleRecurrenceNotesChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="overline" color="text.secondary">{t('taskPage.field.recurrenceAttachments')}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {currentTask.taskRecurrences.find((recurrence) => !recurrence.completed)?.attachments?.map((attachment, idx) => (
                                                <FormControl key={idx}>
                                                    <a href={attachment.url} target="_blank" rel="noreferrer">{attachment.filename}</a>
                                                </FormControl>
                                            ))}
                                            {(currentTask.taskRecurrences.find((recurrence) => !recurrence.completed)?.attachments?.length === 0) &&
                                                <Typography variant="body2" color="text.secondary">{t('taskPage.field.noAttachments')}</Typography>
                                            }
                                        </Box>
                                        <Button
                                            disabled={!canModifyFields || !(parseInt(taskId) > 0)}
                                            sx={{ mt: 1.5 }}
                                            variant="outlined"
                                            onClick={() => setShowRecurrenceFileUploadModal(true)}
                                        >
                                            {t('taskPage.button.addNewAttachment')}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button variant="outlined" onClick={() => setShowPastRecurrencesModal(true)}>
                                            {t('taskPage.button.getPastCompletions')}
                                        </Button>
                                    </Grid>
                                </Section>
                            )}
                        </Grid>

                        {/* Right column */}
                        <Grid item xs={12} md={4}>
                            <Section title={t('taskPage.section.assignment')}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('fieldName.assignedTo')}
                                        disabled={!canModifyFields}
                                        value={currentTask?.assignedToUserId ?? ''}
                                        onChange={(event) => {
                                            const thisTask = {...currentTask};
                                            thisTask.assignedToUserId = event.target.value;
                                            setCurrentTask(thisTask);
                                        }}
                                    >
                                        {availableUsers.map((user, index) => (
                                            <MenuItem key={index} value={user.userId}>{user.username}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('fieldName.status')}
                                        disabled={!canModifyFields}
                                        value={availableStatuses.some(s => s.stId === currentTask?.statusId) ? currentTask.statusId : ''}
                                        onChange={(event) => {
                                            const thisTask = {...currentTask};
                                            thisTask.statusId = event.target.value;
                                            setCurrentTask(thisTask);
                                        }}
                                    >
                                        {availableStatuses.sort((a,b) => a.stSortSeq - b.stSortSeq).map((status, index) => (
                                            <MenuItem key={index} value={status.stId}>{status.localizedName ?? status.stName}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Section>

                            {parseInt(taskId) > 0 && (
                                <Section title={t('taskPage.section.activity')}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('taskPage.createdBy', { username: currentTask?.reportedUser.username ?? '', date: currentTask?.reportedDate ?? '' })}
                                        </Typography>
                                    </Grid>
                                </Section>
                            )}
                        </Grid>
                    </Grid>

                    {/* ── Tabbed Comments / History / Attachments ──── */}
                    {isExistingTask && (
                        <Box sx={{ mt: 2, px: 1 }}>
                            <Paper sx={{ p: 2 }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={(_e, v) => setTabValue(v)}
                                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                                >
                                    <Tab label={`${t('taskPage.tabs.comments')} (${commentItems.length})`} />
                                    <Tab label={`${t('taskPage.tabs.history')} (${historyItems.length})`} />
                                    <Tab label={`${t('taskPage.tabs.attachments')} (${attachmentItems.length})`} />
                                </Tabs>

                                {/* Comments tab */}
                                {tabValue === 0 && (
                                    <Box>
                                        <Box sx={{ mb: 2 }}>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={{
                                                    licenseKey: 'GPL',
                                                    toolbar: {
                                                        items: [ 'heading',
                                                            '|',
                                                            'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                                            '|',
                                                            'bold', 'italic', 'link', 'bulletedList', 'numberedList',
                                                            '|',
                                                            'blockQuote', 'insertTable', 'mediaEmbed', 'codeBlock',
                                                            '|',
                                                            'todoList', 'outdent', 'indent', 'undo', 'redo' ]
                                                    },
                                                    plugins: [
                                                        Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, List, Heading, Indent, MediaEmbed, Table, FontFamily, FontSize, BlockQuote, AutoLink, LinkEditing, LinkUI
                                                    ],
                                                    link: { addTargetToExternalLinks: true },
                                                    mediaEmbed: { previewsInData: true },
                                                }}
                                                data={currentComment}
                                                onChange={(event, editor) => setCurrentComment(editor.getData())}
                                            />
                                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={saveTaskComment}
                                                    disabled={currentComment === ''}
                                                >
                                                    {t('taskPage.button.saveComment')}
                                                </Button>
                                            </Box>
                                        </Box>
                                        {commentItems.length === 0
                                            ? <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>{t('taskPage.empty.noComments')}</Typography>
                                            : commentItems.map((history, index) => renderTaskHistory(history, index))
                                        }
                                    </Box>
                                )}

                                {/* History tab */}
                                {tabValue === 1 && (
                                    <Box>
                                        {historyItems.length === 0
                                            ? <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>{t('taskPage.empty.noHistory')}</Typography>
                                            : historyItems.map((history, index) => renderTaskHistory(history, index))
                                        }
                                    </Box>
                                )}

                                {/* Attachments tab */}
                                {tabValue === 2 && (
                                    <Box>
                                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <TaskHistoryButtonBar
                                                style={{ justifyContent: 'flex-end' }}
                                                handleInlineImagesButton={handleInlineImagesButton}
                                                inlineImagesButtonText={inlineImagesButtonText}
                                            />
                                        </Box>
                                        {attachmentItems.length === 0
                                            ? <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>{t('taskPage.empty.noAttachments')}</Typography>
                                            : attachmentItems.map((history, index) => renderTaskHistory(history, index))
                                        }
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}

                    {/* Modals (unchanged) */}
                    <ConfirmDeleteDialog
                        id="confirm-delete"
                        keepMounted
                        open={showDeleteModal}
                        onClose={onDeleteClick}
                    />
                    <FileUploadModal
                        onClose={handleFileUpload}
                        open={showUploadModal}
                        imageButton={true}
                        acceptFile={handleFileUpload}
                    />
                    <FileUploadModal
                        onClose={handleRecurrenceFileUpload}
                        open={showRecurrenceFileUploadModal}
                        imageButton={true}
                        acceptFile={handleRecurrenceFileUpload}
                    />
                    <TaskRelationshipModal
                        onClose={handleCloseRelationships}
                        open={openRelationshipModal}
                        taskId={taskId === '' || taskId === '0' ? 0 : parseInt(taskId)}
                    />
                    <TaskSubscriptionModal
                        onClose={handleCloseSubscribers}
                        open={showSubscribersModal}
                        taskId={parseInt(taskId)}
                        availableUsers={availableUsers}
                    />
                    <EditTaskRecurrenceModal
                        open={showEditRecurrenceModal}
                        close={() => setShowEditRecurrenceModal(false)}
                        currentRecurrenceString={recurrenceString}
                        setRecurrenceString={handleRecurrenceStringChange}
                    />
                    <RecurrencePastCompletionsModal
                        taskId={taskId}
                        open={showPastRecurrencesModal}
                        close={() => setShowPastRecurrencesModal(false)}
                        taskApiService={taskApiService}
                    />
                    <ConfirmDeleteDialog
                        onClose={handleCopyTask}
                        open={showConfirmCopy}
                        content={t('taskPage.confirmCopyContent')}
                        title={t('taskPage.confirmCopyTitle')}
                    />
                </Box>
                }
        </Box>
    );
};

export default Task;
